// ABOUTME: Banner component displayed during admin impersonation with exit functionality
// ABOUTME: Shows impersonated user name and provides exit button to return to admin view

import React from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { ExitToApp } from '@mui/icons-material';
import { useImpersonation } from '../hooks/useImpersonation';

const ImpersonationBanner: React.FC = () => {
  const { isImpersonating, impersonatedUser, endImpersonation } = useImpersonation();

  if (!isImpersonating || !impersonatedUser) {
    return null;
  }

  const handleExit = async () => {
    try {
      const userId = impersonatedUser.userId;
      await endImpersonation();

      // Add small delay to ensure sessionStorage is fully cleared
      await new Promise(resolve => setTimeout(resolve, 100));

      // Force a full page reload with replace() to ensure all state is cleared
      window.location.replace(`/admin/users/${userId}`);
    } catch (error) {
      console.error('Failed to exit impersonation:', error);
    }
  };

  return (
    <Alert
      severity="warning"
      data-testid="impersonation-banner"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1300,
        borderRadius: 0,
        '& .MuiAlert-message': {
          width: '100%',
        },
      }}
      action={
        <Button
          color="inherit"
          size="small"
          onClick={handleExit}
          startIcon={<ExitToApp />}
          sx={{
            whiteSpace: 'nowrap',
          }}
        >
          Exit Impersonation
        </Button>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Impersonating: {impersonatedUser.fullName || `User #${impersonatedUser.userId}`}
        </Typography>
        {impersonatedUser.email && (
          <Typography variant="body2" color="text.secondary">
            ({impersonatedUser.email})
          </Typography>
        )}
        <Typography variant="body2" sx={{ ml: 1, fontStyle: 'italic', fontWeight: 600 }}>
          • Read-Only Mode
        </Typography>
      </Box>
    </Alert>
  );
};

export default ImpersonationBanner;
